import React from 'react';
// import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';
import {simplifyQuestions} from 'utils/user_bio';

import {Grid, Stack, Divider, screenSizeSmaller, Box} from 'views/components';

import {RefreshWidget} from 'views/module';

import Filter from './scene/filter/Filter';
import Header from './scene/Header';
import List from './scene/list/List';
import MapFilter from './scene/filter/MapFilter';

import {generateFilterOptions} from './scene/filter/FilterGenerator';
import backend from 'utils/backend';

const filterOptions = generateFilterOptions();

const defaultSelection = () => {
  return {
    order: {orderBy: 'creationTime', orderDirc: 'DESC'},
    filter: {
      status: {
        filterData: filterOptions['status'],
        optionSelected: [
          'ACTIVE',
          'PROFILE_REVIEW',
        ],
      },
      location: {
        filterData: filterOptions['location'],
        optionSelected: 'disabled',
      },
    },
  };
};

const CaregiverList = () => {
  const [reload, setReload] = React.useState(0);
  const [selection, setSelection] = React.useState(defaultSelection());
  const [data, setData] = React.useState([]);
  const [lastEvaluatedKey, setLastEvaluatedKey] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [mapBounds, setMapBounds] = React.useState(null);
  const [highlightedCaregiverId, setHighlightedCaregiverId] = React.useState(null);
  const [caregiversForMap, setCaregiversForMap] = React.useState([]);

  // Used to cancel stale loadData results: increment to mark previous requests stale
  const requestIdRef = React.useRef(0);

  const smallerScreen = screenSizeSmaller('md');

  const resetFilter = () => {
    setSelection(defaultSelection());
  };

  const updateFilter = (filter, option) => {
    setSelection((prev) => {
      const newFilter = {...prev.filter};

      if (option) {
        if (filter.multiSelect) {
          const selected = newFilter[filter.key]?.optionSelected || [];

          const doesItExist = selected.find((k) => k === option.key);
          if (!doesItExist) {
          // Add option
            newFilter[filter.key] = {
              filterData: filter,
              optionSelected: [...selected, option.key],
            };
          } else {
            // Remove option
            newFilter[filter.key] = {
              ...newFilter[filter.key],
              optionSelected: selected.filter((k) => k !== option.key),
            };
          }
        } else {
          // Single select: replace value
          newFilter[filter.key] = {
            filterData: filter,
            optionSelected: option.key,
          };
        }
      } else {
        // Remove filter entirely
        delete newFilter[filter.key];
      }

      setLastEvaluatedKey(null);
      return {...prev, filter: newFilter};
    });
  };

  // Check if map filter is enabled
  const isMapFilterEnabled = selection.filter.location?.optionSelected === 'enabled';

  const loadData = React.useCallback(async () => {
    // Capture current request id so this call can be ignored if a newer one starts
    const reqId = requestIdRef.current;
    setIsLoading(true);
    try {
      const filter = [];
      for (const filterSelected of Object.values(selection.filter)) {
        if (filterSelected.filterData.key === 'location') continue;

        if (filterSelected.filterData.key === 'certification') {
          filter.push(...filterSelected.optionSelected.map((optKey) => ({key: optKey, value: true})));
          continue;
        }

        if (filterSelected.filterData.key === 'languages') {
          const labels = filterSelected.optionSelected.map((k) => filterOptions.languages.options[k].label);
          filter.push({key: 'languages', value: labels.join(' ')});
          continue;
        }

        filter.push({key: filterSelected.filterData.key, value: filterSelected.optionSelected});
      }

      // Add buffer bounding box filters when map filter is enabled and buffer bounds are set
      if (isMapFilterEnabled && mapBounds) {
        filter.push({key: 'latitude', value: `[${mapBounds.minLat} TO ${mapBounds.maxLat}]`});
        filter.push({key: 'longitude', value: `[${mapBounds.minLng} TO ${mapBounds.maxLng}]`});
      }

      const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};
      const result = await backend.caregiver.queryCaregiver(filter, sortBy, lastEvaluatedKey);

      // If another request started after this one, drop the results (cancelled/stale)
      if (reqId !== requestIdRef.current) return;

      setData((e) => [...e, ...result.list]);
      setLastEvaluatedKey(result.lastEvaluatedKey);
    } finally {
      // Only update loading state when this call is still the latest
      if (reqId === requestIdRef.current) setIsLoading(false);
    }
  }, [selection, lastEvaluatedKey, mapBounds]);

  React.useEffect(() => {
    // Bump request id to mark any in-flight requests stale/cancelled
    requestIdRef.current += 1;

    setData([]);
    setLastEvaluatedKey(null);
    setHighlightedCaregiverId(null);
    loadData();
  }, [selection, reload, mapBounds]); // Reset when bounds change

  React.useEffect(() => {
    const list = data.map((caregiver) => {
      const bio = simplifyQuestions(caregiver.bio?.questions ?? {});
      const firstName = (bio?.firstName || '').trim();
      const lastName = (bio?.lastName || '').trim();
      const profileImg = bio?.profilePhoto?.[0]?.src;

      const name = [firstName, lastName].filter(Boolean).join(' ') ||
                   caregiver.caregiverName || 'Caregiver';

      return {
        id: caregiver.caregiverRn,
        name,
        profileImg,
        location: {
          lat: caregiver.latitude || null,
          lng: caregiver.longitude || null,
        },
      };
    }).filter((c) => c.location.lat && c.location.lng);

    setCaregiversForMap(list);
  }, [data]);


  const onBoundChange = (e) => {
    setMapBounds(e);
    setLastEvaluatedKey(null);
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Header />
      </Grid>
      <Grid item xs={12}>
        <Divider/>
      </Grid>
      <Grid item xs={12}>
        <Stack spacing={2} direction={{md: 'row', xs: 'column-reverse'}}>
          <Filter
            selection={selection}
            filterOptions={filterOptions}
            updateFilter={updateFilter}
            onClearFilter={resetFilter}
          />
          <RefreshWidget
            numRefresh={reload}
            setNumRefresh={setReload}
            justifyContent={smallerScreen ? 'start' : 'end'}
          />
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={isMapFilterEnabled ? 6 : 12}>
            {
              isMapFilterEnabled &&
              <Box sx={{height: {xs: 'calc(100vh - 220px)', md: 'calc(100vh - 160px)'}, overflowY: 'auto', pr: 1}}>
                <List
                  data={data}
                  isLoading={isLoading}
                  loadMore={loadData}
                  hasMore={!!lastEvaluatedKey}
                  highlightedCaregiverId={highlightedCaregiverId}
                />
              </Box>
            }
            {
              !isMapFilterEnabled &&
              <List
                data={data}
                isLoading={isLoading}
                loadMore={loadData}
                hasMore={!!lastEvaluatedKey}
                highlightedCaregiverId={highlightedCaregiverId}
              />
            }
          </Grid>
          {
            isMapFilterEnabled &&
            <Grid item xs={12} md={6}>
              <MapFilter
                caregivers={caregiversForMap}
                onBoundsChange={onBoundChange}
                onMarkerClick={setHighlightedCaregiverId}
              />
            </Grid>
          }
        </Grid>
      </Grid>
    </Grid>
  );
};
CaregiverList.propTypes = {
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(CaregiverList));
